/**
 * VISTOERA_TENANT_ADMIN_360_..._P0_V1 (task spec §25/§72/§73) — Tenant
 * 360 Overview's "积分消耗趋势" line/area chart.
 *
 * No charting library exists in this repo (confirmed by forensic audit
 * of `package.json` + the existing `MarketingTrendChart`/`TrendLineChart`/
 * `DonutGauge` precedent, all hand-rolled SVG) — a single-series line
 * for <=90 daily points does not warrant adding one. Modeled directly
 * on `MarketingTrendChart`'s layout (never a diverging second copy of
 * the axis/label math), reduced to one series.
 */
export interface CreditTrendPoint {
  date: string
  consumed: number
}

export default function CreditTrendChart({
  points,
  seriesLabel,
  emptyLabel,
  formatDate,
  formatValue,
  height = 220,
}: {
  points: CreditTrendPoint[]
  seriesLabel: string
  emptyLabel: string
  formatDate: (d: string) => string
  formatValue: (v: number) => string
  height?: number
}) {
  const hasAnyConsumption = points.some((p) => p.consumed > 0)
  if (points.length === 0 || !hasAnyConsumption) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          fontSize: 13,
        }}
      >
        {emptyLabel}
      </div>
    )
  }

  const width = 680
  const padding = { top: 16, right: 16, bottom: 28, left: 8 }
  const innerWidth = width - padding.left - padding.right
  const innerHeight = height - padding.top - padding.bottom

  const color = 'var(--pf-primary, #5B5FF5)'

  const maxValue = Math.max(...points.map((p) => p.consumed), 1)
  const range = maxValue || 1

  const xStep = points.length > 1 ? innerWidth / (points.length - 1) : 0
  const coords = points.map((p, i) => ({
    x: padding.left + i * xStep,
    y: padding.top + innerHeight - (p.consumed / range) * innerHeight,
  }))

  const linePath = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L${coords[coords.length - 1].x.toFixed(1)},${(padding.top + innerHeight).toFixed(1)} L${coords[0].x.toFixed(1)},${(padding.top + innerHeight).toFixed(1)} Z`

  const labelCount = Math.min(6, points.length)
  const labelIndices =
    labelCount <= 1 ? [0] : Array.from({ length: labelCount }, (_, i) => Math.round((i * (points.length - 1)) / (labelCount - 1)))

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 4, fontSize: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)' }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, display: 'inline-block' }} />
          {seriesLabel}
        </span>
      </div>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={seriesLabel}>
        <path d={areaPath} fill={color} opacity={0.1} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} />
        {coords.length <= 31 && coords.map((c, i) => <circle key={i} cx={c.x} cy={c.y} r={2.2} fill={color} />)}
        {labelIndices.map((idx) => (
          <text
            key={idx}
            x={coords[idx].x}
            y={height - 8}
            fontSize={11}
            fill="var(--text-muted)"
            textAnchor={idx === 0 ? 'start' : idx === points.length - 1 ? 'end' : 'middle'}
          >
            {formatDate(points[idx].date)}
          </text>
        ))}
        <title>{points.map((p) => `${formatDate(p.date)}: ${formatValue(p.consumed)}`).join('\n')}</title>
      </svg>
    </div>
  )
}
